import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { COMMERCE_ERROR, CommerceError, isCommerceError } from '@loop-coffee/commerce-contracts';

import { createClient } from './client';
import { cartStorage } from './cartStorage';
import { readCommerceConfig } from './config';
import { createShippingAdapter } from './shipping/options';
import { createCheckoutPaymentAdapter, loadCheckoutPaymentOptions } from './payments/checkout';
import { pendingPaymentStorage } from './payments/pendingPayment';

const DISABLED = Object.freeze({ enabled: false });

const leaveForPayment = (url) => window.location.assign(url);

const CommerceContext = createContext(DISABLED);

export const useCommerce = () => useContext(CommerceContext);

export const itemCount = (cart) => (cart?.items || []).reduce((sum, item) => sum + item.quantity, 0);

/**
 * Owns the Store API client and the persisted cart. `client`, `storage` and the shipping adapter
 * are injectable so tests and the shipping epic can replace them. Payment methods come from the
 * region's providers through the payments registry; the customer picks one with `selectPayment`.
 * Redirect flows keep the placed order in `pendingPayment` and leave through `redirect(url)`.
 */
export function CommerceProvider({
    children,
    config = readCommerceConfig(),
    client,
    storage = cartStorage,
    shippingAdapter,
    pendingPayment = pendingPaymentStorage,
    redirect = leaveForPayment,
}) {
    const enabled = Boolean(config.enabled);
    const { baseUrl, publishableKey } = config;
    const api = useMemo(() => {
        if (!enabled) return null;
        return client || createClient({ baseUrl, publishableKey });
    }, [enabled, client, baseUrl, publishableKey]);
    const shipping = useMemo(() => api && (shippingAdapter || createShippingAdapter(api)), [api, shippingAdapter]);

    // status: loading | ready | error (only the cart restore; actions report through `error`)
    const [cart, setCart] = useState(null);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [pending, setPending] = useState(false);
    const [order, setOrder] = useState(null);
    const [payment, setPayment] = useState(null);
    const [redirectUrl, setRedirectUrl] = useState(null);
    const regionId = useRef(config.regionId || null);

    const resolveRegionId = useCallback(async () => {
        if (regionId.current) return regionId.current;
        const regions = await api.listRegions();
        const region = regions.find((r) => (r.countries || []).some((c) => c.iso_2 === config.countryCode)) || regions[0];
        if (!region) {
            throw new CommerceError(COMMERCE_ERROR.UNAVAILABLE, 'Nenhuma região de venda configurada');
        }
        regionId.current = region.id;
        return region.id;
    }, [api, config.countryCode]);

    const expire = useCallback(() => {
        storage.clear();
        setCart(null);
        setNotice('expired');
    }, [storage]);

    const restore = useCallback(async () => {
        const cartId = storage.get();
        if (!cartId) {
            setCart(null);
            setStatus('ready');
            return;
        }
        setStatus('loading');
        setError(null);
        try {
            const restored = await api.getCart(cartId);
            if (restored.completed_at) {
                expire();
            } else {
                setCart(restored);
            }
            setStatus('ready');
        } catch (e) {
            if (isCommerceError(e, COMMERCE_ERROR.NOT_FOUND)) {
                expire();
                setStatus('ready');
            } else {
                // Keep the stored id: an outage must not throw the customer's cart away.
                setError(e);
                setStatus('error');
            }
        }
    }, [api, storage, expire]);

    useEffect(() => {
        if (api) restore();
    }, [api, restore]);

    // A rejected change (stock/price revalidated by the API) re-reads the cart so the UI shows the
    // backend state instead of the optimistic one.
    const fail = useCallback((e) => {
        setError(e);
        if (isCommerceError(e, COMMERCE_ERROR.NOT_FOUND)) {
            expire();
        } else if (isCommerceError(e, COMMERCE_ERROR.INVALID) && storage.get()) {
            api.getCart(storage.get()).then(setCart, () => {});
        }
    }, [api, storage, expire]);

    // Runs a cart mutation; returns the new cart, or null when it failed (see `error`).
    const mutate = useCallback(async (operation) => {
        setPending(true);
        setError(null);
        try {
            const next = await operation();
            setCart(next);
            return next;
        } catch (e) {
            fail(e);
            return null;
        } finally {
            setPending(false);
        }
    }, [fail]);

    const ensureCart = useCallback(async () => {
        if (cart && storage.get() === cart.id) return cart;
        const created = await api.createCart({ regionId: await resolveRegionId() });
        storage.set(created.id);
        setNotice(null);
        return created;
    }, [api, cart, storage, resolveRegionId]);

    const addItem = useCallback((variantId, quantity = 1) => mutate(async () => {
        const current = await ensureCart();
        try {
            return await api.addLineItem(current.id, { variantId, quantity });
        } catch (e) {
            if (!isCommerceError(e, COMMERCE_ERROR.NOT_FOUND)) throw e;
            // The stored cart expired between the restore and the click: start a new one once.
            storage.clear();
            const fresh = await api.createCart({ regionId: await resolveRegionId() });
            storage.set(fresh.id);
            return api.addLineItem(fresh.id, { variantId, quantity });
        }
    }), [api, mutate, ensureCart, storage, resolveRegionId]);

    const removeItem = useCallback(
        (lineId) => mutate(() => api.removeLineItem(cart.id, lineId)),
        [api, cart, mutate],
    );

    const updateQuantity = useCallback((lineId, quantity) => {
        if (quantity <= 0) return removeItem(lineId);
        return mutate(() => api.updateLineItem(cart.id, lineId, quantity));
    }, [api, cart, mutate, removeItem]);

    const setAddress = useCallback(({ email, cpf_cnpj: taxId, ...fields }) => {
        // Medusa addresses have no document column; `metadata.cpf_cnpj` is read by the Melhor Envio
        // label and by the Asaas customer data.
        const address = { ...fields, country_code: config.countryCode, metadata: { cpf_cnpj: String(taxId ?? '').replace(/\D/g, '') } };
        return mutate(() => api.updateCart(cart.id, {
            email: email.trim(),
            shipping_address: address,
            billing_address: address,
        }));
    }, [api, cart, config.countryCode, mutate]);

    const selectShipping = useCallback(
        (optionId) => mutate(() => shipping.select(cart, optionId)),
        [cart, shipping, mutate],
    );

    const cartRegionId = cart?.region_id;
    const listPaymentOptions = useCallback(
        async () => loadCheckoutPaymentOptions(api, cartRegionId || await resolveRegionId()),
        [api, cartRegionId, resolveRegionId],
    );

    const selectPayment = useCallback((providerId) => {
        try {
            setPayment(createCheckoutPaymentAdapter(api, providerId));
            return true;
        } catch (cause) {
            setPayment(null);
            setError(new CommerceError(COMMERCE_ERROR.INVALID, 'Forma de pagamento indisponível.', { cause }));
            return false;
        }
    }, [api]);

    const placeOrder = useCallback(async () => {
        if (!payment) {
            setError(new CommerceError(COMMERCE_ERROR.INVALID, 'Escolha uma forma de pagamento.'));
            return null;
        }
        setPending(true);
        setError(null);
        try {
            const prepared = await payment.prepare(cart);
            const leavesToPay = payment.flow === 'redirect';
            const result = await api.completeCart(cart.id);
            if (result.type === 'order') {
                storage.clear();
                setCart(null);
                if (leavesToPay) {
                    // The order exists and waits for the provider; the return page follows its status.
                    pendingPayment.set({ orderId: result.order.id, checkoutUrl: prepared.redirectUrl });
                    setOrder(result.order);
                    setRedirectUrl(prepared.redirectUrl);
                    redirect(prepared.redirectUrl);
                    return result.order;
                }
                // Without the status the confirmation shows the payment as pending, never as paid.
                const placed = await api.getOrder(result.order.id).then(
                    (detail) => ({ ...result.order, payment_status: detail.payment_status }),
                    () => result.order,
                );
                setOrder(placed);
                return placed;
            }
            setCart(result.cart);
            setError(new CommerceError(
                COMMERCE_ERROR.INVALID,
                result.error?.message || 'O pedido não pôde ser concluído. Revise o carrinho.',
            ));
            return null;
        } catch (e) {
            fail(e);
            return null;
        } finally {
            setPending(false);
        }
    }, [api, cart, payment, storage, pendingPayment, redirect, fail]);

    const value = useMemo(() => {
        if (!enabled) return DISABLED;
        return {
            enabled,
            client: api,
            shipping,
            payment,
            listPaymentOptions,
            selectPayment,
            resolveRegionId,
            cart,
            status,
            error,
            notice,
            pending,
            order,
            redirectUrl,
            pendingPayment,
            restore,
            addItem,
            removeItem,
            updateQuantity,
            setAddress,
            selectShipping,
            placeOrder,
            dismissError: () => setError(null),
        };
    }, [enabled, api, shipping, payment, listPaymentOptions, selectPayment, resolveRegionId, cart, status, error, notice, pending, order,
        redirectUrl, pendingPayment, restore, addItem, removeItem, updateQuantity, setAddress, selectShipping, placeOrder]);

    return <CommerceContext.Provider value={value}>{children}</CommerceContext.Provider>;
}

/** Loads the published catalog with region prices and stock from the Store API. */
export function useCatalog() {
    const { enabled, client, resolveRegionId } = useCommerce();
    // status: loading | ready | empty | error
    const [state, setState] = useState({ status: 'loading', products: [], error: null });

    const load = useCallback(async () => {
        setState({ status: 'loading', products: [], error: null });
        try {
            const products = await client.listProducts({ regionId: await resolveRegionId() });
            setState({ status: products.length ? 'ready' : 'empty', products, error: null });
        } catch (error) {
            setState({ status: 'error', products: [], error });
        }
    }, [client, resolveRegionId]);

    useEffect(() => {
        if (enabled) load();
    }, [enabled, load]);

    return { ...state, retry: load };
}
